<template>
  <div class="modal-overlay">
    <div class="context">

      <div class="review-logo-container">
        <img :src="reviewImage" alt="Image icon" />
      </div>

      <p>Do you want to {{ verb }} this {{ subject }} for an interview?</p>
      <h6>If Yes, fill in the information below</h6>

      <div class="comment-section">
        <div class="inputs-holder">

          <div class="inputs-inner-wrapper title-and-date">
            <div class="form-field-holder">
              <h3>
                Title
              </h3>
              <input
                type="text"
                name="interviewTitle"
                @change="validateInput"
                v-model="formData.interviewTitle"
              />
              <span
                class="error-message"
                v-if="interviewError.interviewTitleError"
              >
                * Title is required
              </span>
            </div>
            <div class="form-field-holder">
              <h3>
                Date
              </h3>
              <input
                name="interviewDate"
                type="date"
                @change="validateInput"
                v-model="formData.interviewDate"
              />
              <span class="validity"></span>
              <span
                class="error-message"
                v-if="interviewError.interviewDateError"
              >
                * Date is required
              </span>
            </div>
          </div>

          <div class="inputs-inner-wrapper link-and-comment">
            <div class="form-field-holder">
              <h3>
                Time
              </h3>
              <input
                name="interviewTime"
                type="time"
                @change="validateInput"
                v-model="formData.interviewTime"
                min="08:00"
                max="18:00"
                pattern="[0-9]{2}:[0-9]{2}"
                step="any"
                required
              />
              <span class="validity"></span>
              <span
                  class="error-message"
                  v-if="interviewError.interviewTimeError"
                >
                  * Time is required
                </span>
            </div>
            <div class="form-field-holder">
              <h3>
                Interview Link
              </h3>
              <input
                name="interviewLink"
                type="url"
                @change="validateInput"
                v-model="formData.interviewLink"
                placeholder="e.g Zoom, Google Meet, or Skype link — begin with https:// or http://"
              />
              <span
                class="error-message"
                v-if="interviewError.interviewLinkError"
              >
                * Link is required and should be valid
              </span>
            </div>
            <div class="form-field-holder">
              <h3>
                Any short note (for the candidate)?
              </h3>
              <label for="interviewComment" class="form-label">
                You can customize the message by replacing the following placeholders: [Date], [Time], [Link], and [Extras].
                When you fill in the date, time, and link fields, the placeholders will automatically update in the text area below.
              </label>
              <textarea
                name="interviewComment"
                @change="validateInput"
                v-model="formData.interviewComment"
                placeholder="For multiple comments, press the enter key"
                id="commentTextarea"
              >
              </textarea>
              <span
                class="error-message"
                v-if="interviewError.interviewCommentError"
              >
                * Comment is required
              </span>
            </div>
          </div>

        </div>
      </div>

      <div class="buttons">
        <button @click="modalAction('cancel')">Cancel</button>
        <button @click="modalAction('confirm')">Confirm</button>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'ScheduleInterviewModal',
  props: ['verb', 'subject', 'reviewImage', 'candidateName', 'companyName', 'jobTitle'],
  data() {
    return {
      baseTemplate: '', // Will store the master string copy
      isUserEdited: false, // Prevents overwriting what a user typed
      formData: {
        interviewTitle: null,
        interviewDate: null,
        interviewTime: null,
        interviewLink: null,
        interviewComment: '',
      },
      interviewError: {
        interviewTitleError: false,
        interviewDateError: false,
        interviewTimeError: false,
        interviewLinkError: false,
        interviewCommentError: false,
      }
    };
  },
  created() {
    // Safely assign properties once props are bound
    this.baseTemplate = `Hello ${this.candidateName || '[Candidate Name]'},
You are getting this interview invitation email because you applied for the ${this.jobTitle || '[Job]'} role at ${this.companyName || '[CompanyName]'} via TalentApp.

If you have already completed an interview for this role, no further action is required. If you have not yet interviewed, please add the following interview schedule to your calendar and join using the details provided below.

Interview Type: Virtual
Date: [Date]
Time: [Time]
Meeting Link: [Link]
[Extras: any additional information from the employer]

If you are unable to make this interview schedule, please notify us as soon as possible via email and provide your three earliest available times.

Regards,
${this.companyName || '[CompanyName]'} Team`;

    // Set initial layout string
    this.formData.interviewComment = this.baseTemplate;
  },
  watch: {
    // Dynamic watch rules that trigger whenever user fills inputs
    'formData.interviewDate': function() { this.updateTemplateText(); },
    'formData.interviewTime': function() { this.updateTemplateText(); },
    'formData.interviewLink': function() { this.updateTemplateText(); }
  },
  methods: {
    userIsTyping() {
      // Locks the text area so auto updates don't break what the user customized
      this.isUserEdited = true;
    },
    updateTemplateText() {
      if (this.isUserEdited) return;

      let freshString = this.baseTemplate;

      if (this.formData.interviewDate) {
        // Formats string to user-friendly layout like "Thu Jun 18 2026"
        const friendlyDate = new Date(this.formData.interviewDate).toDateString();
        freshString = freshString.replace('[Date]', friendlyDate);
      }
      if (this.formData.interviewTime) {
        freshString = freshString.replace('[Time]', this.formData.interviewTime);
      }
      if (this.formData.interviewLink) {
        freshString = freshString.replace('[Link]', this.formData.interviewLink);
      }

      this.formData.interviewComment = freshString;
    },
    modalAction(value) {
      if (value === 'cancel') {
        this.$emit('modalAction', {value: value});
      } else if(value === 'confirm' &&
        this.interviewError.interviewTitleError ||
        this.interviewError.interviewDateError ||
        this.interviewError.interviewTimeError ||
        this.interviewError.interviewLinkError ||
        this.interviewError.interviewCommentError ||
        !this.formData.interviewTitle ||
        !this.formData.interviewDate ||
        !this.formData.interviewTime ||
        !this.formData.interviewLink ||
        !this.formData.interviewComment
      ) {
        Object.keys(this.formData).forEach((field) => {
          if (!this.formData[field]) {
            this.interviewError[`${field}Error`] = true;
          }
        });
      } else {
        this.$emit('modalAction', {value: value, reviewComment: this.formData});
      }
    },
    validateInput(e) {
      let fieldName = e.target.name;
      let fieldValue = e.target.value;
      switch (fieldName) {
        case 'interviewTitle':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.interviewError.interviewTitleError = true;
          } else {
            this.interviewError.interviewTitleError = false;
          }
          break;
        case 'interviewDate':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.interviewError.interviewDateError = true;
          } else {
            fieldValue = new Date(fieldValue).toDateString();
            this.interviewError.interviewDateError = false;
          }
          break;
        case 'interviewTime':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.interviewError.interviewTimeError = true;
          } else {
            this.interviewError.interviewTimeError = false;
          }
          break;
        case 'interviewLink':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.interviewError.interviewLinkError = true;
          } else if (!this.validateUrl(fieldValue)) {
            this.interviewError.interviewLinkError = true;
          }
          else {
            this.interviewError.interviewLinkError = false;
          }
          break;
        case 'interviewComment':
          if (fieldValue.trim() === '' || fieldValue.trim().length < 3) {
            this.interviewError.interviewCommentError = true;
          } else {
            this.interviewError.interviewCommentError = false;
          }
          break;
      }
    },
    validateUrl(string) {
      // This validation function has some inefficiencies while using it
      // until a better solution is implemented, it will be temporarily added here.
      try {
        return Boolean(new URL(string));
      }
      catch {
        return false;
      }
    }
  },
};
</script>

<style scoped>
.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: rgba(20, 20, 20, 0.83);
  display: flex;
  flex-direction: column;
}
.modal-overlay .context {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 21;
  width: 100%;
  height: 100%;
  background: var(--white);
  max-width: 1400px;
  max-height: 990px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 100px;
  overflow-y: scroll;
}
.modal-overlay .context::-webkit-scrollbar {
    display: none;
  }

/* Hide scrollbar for IE, Edge and Firefox */
.modal-overlay .context {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
.modal-overlay .context .review-logo-container {
  margin-top: 40px;
  margin-bottom: 40px;
}
.modal-overlay .context p {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 800;
  font-size: 24px;
  line-height: 29px;
  color: var(--black);
  margin-bottom: 50px;
  text-align: center;
}
.modal-overlay .context h6 {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: normal;
  font-size: 19px;
  line-height: 23px;
  text-align: center;
  color: #707070;
}
.modal-overlay .context .comment-section {
  width: 100%;
  margin-top: 60px;
}
.modal-overlay .context .comment-section h4 {
  font-family: 'montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 21px;
  color: #707070;
  margin-bottom: 15px;
}
.inputs-holder {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
}
.inputs-inner-wrapper {
  width: 40%;
}
.form-field-holder {
  margin-bottom: 40px;
}
.form-field-holder h3 {
  font-family: 'montserrat';
  font-style: normal;
  margin-bottom: 5px;
  font-weight: 500;
  font-size: 18px;
  line-height: 19px;
  color: #707070;
}
.modal-overlay .context .comment-section textarea,
input {
  background: var(--white);
  box-shadow: 0px 0px 4px 2px rgba(180, 180, 180, 0.15);
  border-radius: 5px;
  outline: none;
  padding: 15px;
  width: 100%;
  color: #707070;
}
::placeholder {
  font-size: 12px;
}
textarea {
  resize: none;
  height: 150px;
  line-height: 31px;
}
.modal-overlay .context .comment-section .error-message {
  color: tomato;
  font-size: 12px;
}
.buttons {
  display: flex;
  justify-content: right;
  margin-top: 30px;
  width: 90%;
}
.modal-overlay .context .buttons > button {
  border-radius: 5px;
  padding: 15px 50px;
  font-weight: 800;
}
.modal-overlay .context .buttons :nth-child(2) {
  background: var(--colorPrimary);
  box-shadow: 0px 9px 29px rgba(26, 107, 158, 0.1);
  color: var(--white);
}
.modal-overlay .context .buttons :nth-child(1) {
  color: #252525;
  box-sizing: border-box;
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.15);
  margin-right: 40px;
}
input:invalid+span.validity:after {
  position: absolute;
  content: '✖';
  padding-left: 10px;
  color: tomato;
  padding-top: 15px;
}
input:valid+span.validity:after {
  position: absolute;
  content: '✓';
  padding-left: 10px;
  color: green;
  padding-top: 15px;
}
.form-label {
  font-size: 0.65rem;
  color: #707070;
  line-height: 1.4rem;
}
#commentTextarea {
  margin-top: 10px;
}

/* Responsive Design */
@media only screen and (max-width: 767px) {
  .inputs-holder {
    flex-direction: column;
    justify-content: space-around;
  }
  .inputs-inner-wrapper {
    width: 100%;
  }
  .buttons {
    width: 100%;
  }
}
@media only screen and (max-width: 565px) {
  .buttons {
    justify-content: center;
  }
  .modal-overlay .context {
    padding: 50px;
  }
}
</style>
